import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { admin, type SessionSilver, type SilverCycle } from '../../api/admin'
import { ApiError } from '../../api/client'

const CYCLES_PREVIEW = 60

const STATE_LABEL: Record<SessionSilver['state'], { label: string; className: string }> = {
  measured: { label: 'Medida', className: 'tag-ok' },
  no_cycles: { label: 'Sin ciclos', className: 'tag-warn' },
  processing: { label: 'Procesando', className: 'tag-info' },
  no_signal: { label: 'Sin señal', className: 'tag' },
}

function str(v: unknown): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function num(v: number | null | undefined, digits = 3): string {
  return v == null ? '—' : v.toFixed(digits)
}

/** Qué significa cada `clock_quality` del manifest, en una frase. */
const CLOCK_QUALITY_LABEL: Record<string, { label: string; ok: boolean }> = {
  synced_drift: { label: 'offset + deriva ajustados', ok: true },
  synced_unstable: { label: 'ajustado pero inestable (salto de reloj)', ok: false },
  synced_offset_only: { label: 'una sola probe: offset sin deriva', ok: false },
  quest_only: { label: 'sin sincronizar: sólo el reloj del Quest', ok: false },
  browser_local: { label: 'no hace falta: la fisio comparte el reloj del gameplay', ok: true },
}

function n(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** Estado del eje temporal Quest↔RPi, explicado con los números del manifest.
 *
 *  Hasta 2026-09-07 esta pantalla mostraba `quest_only` como un string gris
 *  más, indistinguible de un valor sano: una sesión con kit que nunca logró
 *  sincronizar relojes se descubría auditando bronces. Ahora la falla dice
 *  qué pasó (no salió ninguna probe / salieron y no volvieron / volvieron y
 *  silver las rechazó) y en qué queda la sesión. */
export function ClockAxisBanner({ manifest }: { manifest: Record<string, unknown> }) {
  const model = (manifest.clock_model ?? {}) as Record<string, unknown>
  const quality = (manifest.effective_clock_quality ?? manifest.clock_quality) as
    | string
    | undefined
  const expected = manifest.clock_probes_expected
  // Bronce viejo (sin `clock_probes_expected`): no se puede afirmar nada.
  if (expected !== true) return null
  const meta = quality ? CLOCK_QUALITY_LABEL[quality] : undefined
  if (meta?.ok) return null

  const sent = n(manifest.clock_probes_sent) ?? 0
  const accepted = n(model.probes_accepted) ?? 0
  const rejected = n(model.probes_rejected) ?? 0
  const cause =
    sent === 0 && accepted === 0
      ? 'el casco no puso ni una probe en el cable: el driver de sincronía no corrió (¿la sesión se jugó sin kit elegido?).'
      : accepted === 0
        ? `salieron ${sent} probes y no volvió ninguna completa: la Pi no contestó (caída, WS reconectando, o el kit lo tomó otro cliente).`
        : `de ${sent} probes enviadas silver aceptó ${accepted} y descartó ${rejected}: no alcanzan para ajustar el eje.`

  return (
    <div className="banner-err mb-md">
      <strong>Relojes Quest↔Pi sin sincronizar</strong> — {cause} La fisiología de
      esta sesión no se puede alinear con el gameplay
      {quality ? ` (clock_quality: ${quality})` : ''}; los ciclos de regulación salen
      nulos. Ver vrs-docs/36 §3.2.
    </div>
  )
}

/** Card "Datos silver": estado + calidad + conteos + tabla compacta de
 *  ciclos, con descarga del zip de artefactos y link a trials.csv. */
function SilverCard({ sessionId }: { sessionId: string }) {
  const [silver, setSilver] = useState<SessionSilver | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    setSilver(null)
    setError(null)
    admin.getSessionSilver(sessionId)
      .then(setSilver)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Error de red.'))
  }, [sessionId])

  const actions = (
    <div className="flex flex-wrap items-center gap-sm">
      <a
        href={admin.sessionArtifactsZipUrl(sessionId)}
        download
        className="btn-primary no-underline"
      >
        Descargar artefactos (bronce + silver)
      </a>
      <a
        href={admin.trialsCsvUrl({ session_id: sessionId })}
        download
        className="btn no-underline"
      >
        trials.csv de esta sesión
      </a>
    </div>
  )

  if (error) {
    return (
      <div className="card p-md">
        <div className="banner-err mb-md">{error}</div>
        {actions}
      </div>
    )
  }
  if (!silver) return <p className="text-sm text-text-muted">Cargando silver…</p>

  const state = STATE_LABEL[silver.state] ?? { label: silver.state, className: 'tag' }
  const manifest = silver.manifest ?? {}
  const physioSource = manifest.physio_source ?? silver.physio_manifest?.source
  const clockQuality = manifest.effective_clock_quality ?? silver.silver_clock_quality
  const qualityCounts = silver.counts.cardio_quality_counts ?? {}
  const cycles: SilverCycle[] = showAll ? silver.cycles : silver.cycles.slice(0, CYCLES_PREVIEW)
  const hidden = silver.cycles.length - cycles.length

  return (
    <div className="card p-md">
      <div className="mb-md flex flex-wrap items-center justify-between gap-md">
        <div className="flex flex-wrap items-center gap-sm">
          <span className={state.className}>{state.label}</span>
          {silver.silver_materialized_at && (
            <span className="text-xs text-text-muted">
              materializado {new Date(silver.silver_materialized_at).toLocaleString()}
            </span>
          )}
        </div>
        {actions}
      </div>

      <ClockAxisBanner manifest={manifest} />

      <div className="mb-md grid grid-cols-2 gap-md sm:grid-cols-4">
        <div className="stat-card">
          <div className="stat-card-label">Calidad de reloj</div>
          <div
            className="stat-card-value"
            title={
              typeof clockQuality === 'string'
                ? CLOCK_QUALITY_LABEL[clockQuality]?.label
                : undefined
            }
          >
            {typeof clockQuality === 'string' &&
            CLOCK_QUALITY_LABEL[clockQuality]?.ok === false ? (
              <span className="tag-err">{clockQuality}</span>
            ) : (
              str(clockQuality)
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Fuente físio</div>
          <div className="stat-card-value">{str(physioSource)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Muestras físio</div>
          <div className="stat-card-value">{str(silver.counts.physio_samples)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Ciclos</div>
          <div className="stat-card-value">{silver.counts.cycles}</div>
        </div>
      </div>

      {Object.keys(qualityCounts).length > 0 && (
        <p className="mb-md flex flex-wrap items-center gap-sm text-sm">
          <span className="text-text-muted">Calidad cardio:</span>
          {Object.entries(qualityCounts).map(([k, v]) => (
            <span key={k} className={k === 'ok' ? 'tag-ok' : 'tag-warn'}>
              {k} × {v}
            </span>
          ))}
        </p>
      )}

      {silver.silver_path && (
        <p className="mb-md text-xs text-text-muted">
          <code>{silver.silver_path}</code>
        </p>
      )}

      {silver.cycles.length === 0 ? (
        <p className="text-sm text-text-muted">
          {silver.state === 'processing'
            ? 'Hay bronce fisiológico pero el silver todavía no corrió.'
            : silver.state === 'no_signal'
              ? 'La sesión no tiene bronce fisiológico (se jugó sin sensores).'
              : 'El silver corrió pero no hubo ciclos que medir.'}
        </p>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>trial_id</th>
                  <th>r_cardio</th>
                  <th>r_cardio_eda</th>
                  <th>quality.cardio</th>
                </tr>
              </thead>
              <tbody>
                {cycles.map((c, i) => (
                  <tr key={`${c.trial_id ?? 'x'}-${i}`}>
                    <td className="text-text-muted">{i + 1}</td>
                    <td><code>{c.trial_id ?? '—'}</code></td>
                    <td>{num(c.r_cardio)}</td>
                    <td>{num(c.r_cardio_eda)}</td>
                    <td>
                      <span className={c.quality?.cardio === 'ok' ? 'tag-ok' : 'tag-warn'}>
                        {c.quality?.cardio ?? '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(hidden > 0 || showAll) && silver.cycles.length > CYCLES_PREVIEW && (
            <button
              type="button"
              className="btn-ghost mt-sm"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll
                ? `Ver primeros ${CYCLES_PREVIEW}`
                : `Ver todos (${silver.cycles.length})`}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export function AdminSessionDetailPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>()
  const [data, setData] = useState<{ session: Record<string, unknown>; trials: Array<Record<string, unknown>> } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    admin.session(sessionId)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Error de red.'))
  }, [sessionId])

  if (error) return <div className="banner-err">{error}</div>
  if (!data) return <p className="text-sm text-text-muted">Cargando…</p>

  return (
    <section>
      <p className="mb-sm">
        <Link to="/admin/sessions" className="text-sm text-text-muted">
          ← Sesiones
        </Link>
      </p>
      <h1>Sesión {sessionId}</h1>

      <h2>Datos silver</h2>
      <SilverCard sessionId={sessionId} />

      <h2>Datos</h2>
      <div className="card overflow-x-auto p-md">
        <pre className="m-0 whitespace-pre-wrap break-all text-xs leading-relaxed text-text-secondary">
          {JSON.stringify(data.session, null, 2)}
        </pre>
      </div>

      <h2>Trials ({data.trials.length})</h2>
      <div className="card overflow-x-auto p-md">
        <pre className="m-0 whitespace-pre-wrap break-all text-xs leading-relaxed text-text-secondary">
          {JSON.stringify(data.trials, null, 2)}
        </pre>
      </div>
    </section>
  )
}
